import type {
  HeadlineSlotSegment,
  HeadlineTemplate,
  SourcePack,
} from "@desk/core";
import {
  defaultHeadlineSelections,
  expandHeadlineTemplate,
} from "@desk/core";
import { captionTextFromTokens } from "./edit-helpers.js";
import type { HeadlineFraming } from "../../content/creative-catalogs.js";
import { headlineFramingsForPackId } from "../../content/levels/index.js";

/**
 * The HEADLINE FRAMING cards for a pack: authored where the level curates them
 * (`headlineFramings` in its level.json), otherwise synthesized from that
 * level's headline templates' default expansions
 * so every pack's Caption tool still shows a clean full-headline picker. Risk is
 * heuristic for synthesized packs: a multi-word assertion or a claim-bearing
 * token reads as a checkable claim; a single label/hook reads as deniable spin.
 */
export function headlineFramingsForPack(pack: SourcePack): HeadlineFraming[] {
  const authored = headlineFramingsForPackId(pack.id);
  if (authored) return authored;
  const templates = pack.headlineTemplates ?? [];
  const claimBearing = new Set(
    pack.captionVocabulary.tokens.filter((t) => t.claimIds.length > 0).map((t) => t.id),
  );
  return templates.map((template) => {
    const tokenIds = expandHeadlineTemplate(template, defaultHeadlineSelections(template));
    const checkable = tokenIds.length >= 3 || tokenIds.some((id) => claimBearing.has(id));
    return {
      id: `framing-${template.id}`,
      text: captionTextFromTokens(pack, tokenIds),
      spin: template.label,
      tokenIds,
      risk: checkable ? "checkable" : "deniable",
    };
  });
}

function sameTokens(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Which framing (if any) the current resolved caption tokens correspond to. */
export function matchFramingFromTokens(
  framings: readonly HeadlineFraming[],
  tokenIds: readonly string[],
): HeadlineFraming | null {
  if (tokenIds.length === 0) return null;
  return framings.find((f) => sameTokens(f.tokenIds, tokenIds)) ?? null;
}

export type HeadlineMatch = {
  template: HeadlineTemplate;
  selections: Record<string, string>;
};

function tokensEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Walk a template against a token list; return slot selections when exact match.
 * Longer slot options are tried first to avoid ambiguous short prefixes.
 */
export function matchTemplateSelections(
  template: HeadlineTemplate,
  tokenIds: readonly string[],
): Record<string, string> | null {
  const selections: Record<string, string> = {};
  let i = 0;

  for (const segment of template.segments) {
    if (segment.kind === "fixed") {
      for (const tid of segment.tokenIds) {
        if (tokenIds[i] !== tid) return null;
        i += 1;
      }
      continue;
    }
    const options = [...segment.options].sort(
      (a, b) => b.tokenIds.length - a.tokenIds.length,
    );
    let matched = false;
    for (const option of options) {
      const slice = tokenIds.slice(i, i + option.tokenIds.length);
      if (tokensEqual(slice, option.tokenIds)) {
        selections[segment.id] = option.id;
        i += option.tokenIds.length;
        matched = true;
        break;
      }
    }
    if (!matched) return null;
  }

  if (i !== tokenIds.length) return null;
  return selections;
}

/** Derive selected headline card + slot choices from a resolved caption token list. */
export function matchHeadlineFromTokens(
  templates: readonly HeadlineTemplate[] | undefined,
  tokenIds: readonly string[],
): HeadlineMatch | null {
  if (!templates?.length || tokenIds.length === 0) return null;
  for (const template of templates) {
    const selections = matchTemplateSelections(template, tokenIds);
    if (selections) return { template, selections };
  }
  return null;
}

export function headlineSentence(
  pack: SourcePack,
  template: HeadlineTemplate,
  selections: Readonly<Record<string, string>>,
): string {
  return captionTextFromTokens(pack, expandHeadlineTemplate(template, selections));
}

export function defaultHeadlineSentence(
  pack: SourcePack,
  template: HeadlineTemplate,
): string {
  return headlineSentence(pack, template, defaultHeadlineSelections(template));
}

export function slotSegments(
  template: HeadlineTemplate,
): HeadlineSlotSegment[] {
  return template.segments.filter(
    (s): s is HeadlineSlotSegment => s.kind === "slot",
  );
}

/** Build a full selection map after changing one slot option. */
export function withSlotSelection(
  template: HeadlineTemplate,
  current: Readonly<Record<string, string>>,
  slotId: string,
  optionId: string,
): Record<string, string> {
  const next = { ...defaultHeadlineSelections(template), ...current };
  next[slotId] = optionId;
  return next;
}
